import { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { episodesAPI, configAPI } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import Sidebar from '@/components/Sidebar';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import useSEO from '@/hooks/useSEO';
import { 
  ArrowLeft, Download, Copy, FileText, BookOpen, 
  Share2, Clock, Check, Loader2, Search, X,
  FileDown, FileType, ExternalLink, Quote, Sparkles,
  RefreshCw, Archive, Instagram, Video, Crown, Lock
} from 'lucide-react';
import { ProBadge, samplePreviewData } from '@/components/LockedPreview';

// Export buttons component (defined outside to avoid re-creation)
const ExportButtons = ({ content, filename, type = 'general', copied, onCopy, onDownloadTxt, onDownloadDocx, onDownloadPdf }) => (
  <div className="flex flex-wrap gap-2">
    <Button
      variant="ghost"
      size="sm"
      className="btn-ghost text-xs"
      onClick={() => onCopy(content, type)}
      data-testid={`copy-${type}-btn`}
    >
      {copied === type ? <Check className="w-3 h-3 mr-1" /> : <Copy className="w-3 h-3 mr-1" />}
      Copy
    </Button>
    <Button
      variant="ghost"
      size="sm"
      className="btn-ghost text-xs"
      onClick={() => onDownloadTxt(content, filename)}
    >
      <FileText className="w-3 h-3 mr-1" />
      TXT
    </Button>
    <Button
      variant="ghost"
      size="sm"
      className="btn-ghost text-xs"
      onClick={() => onDownloadDocx(content, filename)}
    >
      <FileType className="w-3 h-3 mr-1" />
      DOCX
    </Button>
    <Button
      variant="ghost"
      size="sm"
      className="btn-ghost text-xs"
      onClick={() => onDownloadPdf(content, filename)}
    >
      <FileDown className="w-3 h-3 mr-1" />
      PDF
    </Button>
  </div>
);

// Advanced export buttons component
const AdvancedExportButtons = ({ content, title, onExportNotion, onExportGoogleDocs, onDownloadMarkdown }) => (
  <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t border-neutral-800">
    <Button
      variant="ghost"
      size="sm"
      className="btn-ghost text-xs"
      onClick={() => onExportNotion(content, title)}
    >
      <ExternalLink className="w-3 h-3 mr-1" />
      Notion
    </Button>
    <Button
      variant="ghost"
      size="sm"
      className="btn-ghost text-xs"
      onClick={() => onExportGoogleDocs(content, title)}
    >
      <ExternalLink className="w-3 h-3 mr-1" />
      Google Docs
    </Button>
    <Button
      variant="ghost"
      size="sm"
      className="btn-ghost text-xs"
      onClick={() => onDownloadMarkdown(content, title)}
    >
      <Download className="w-3 h-3 mr-1" />
      Markdown
    </Button>
  </div>
);

const EpisodeDetail = () => {
  const { id } = useParams();
  useSEO({ title: 'Episode — EpisodeKit', path: `/episode/${id || ''}`, index: false });
  const navigate = useNavigate();
  const { user } = useAuth();
  const [episode, setEpisode] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [highlightedParagraph, setHighlightedParagraph] = useState(null);
  
  // Phase 1-3 feature states
  const [planFeatures, setPlanFeatures] = useState({});
  const [planFeaturesLoading, setPlanFeaturesLoading] = useState(true);
  // Task 2: Regenerate removed - no longer tracking regen status
  const [generating, setGenerating] = useState({});
  const [generatedContent, setGeneratedContent] = useState({
    bestQuotes: null,
    seoTitles: null,
    tiktokCaptions: null,
    instagramCaptions: null,
    youtubeDescription: null,
    youtubeChapters: null,
    youtubePinnedComment: null,
    youtubeShortsHooks: null,
    youtubeSrt: null,
    youtubeSeoTitles: null,
  });
  const [generationErrors, setGenerationErrors] = useState({});

  useEffect(() => {
    let isMounted = true;
    
    const fetchEpisode = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await episodesAPI.getOne(id);
        if (!isMounted) return;
        
        if (!response.data) {
          throw new Error('Episode not found');
        }
        
        setEpisode(response.data);
        
        // Load generated content from episode if exists
        if (response.data.best_quotes) {
          setGeneratedContent(prev => ({ ...prev, bestQuotes: response.data.best_quotes }));
        }
        if (response.data.seo_titles) {
          setGeneratedContent(prev => ({ ...prev, seoTitles: response.data.seo_titles }));
        }
        if (response.data.tiktok_captions) {
          setGeneratedContent(prev => ({ ...prev, tiktokCaptions: response.data.tiktok_captions }));
        }
        if (response.data.instagram_captions) {
          setGeneratedContent(prev => ({ ...prev, instagramCaptions: response.data.instagram_captions }));
        }
        // Load YouTube outputs
        if (response.data.youtube_description) {
          setGeneratedContent(prev => ({ ...prev, youtubeDescription: response.data.youtube_description }));
        }
        if (response.data.youtube_chapters) {
          setGeneratedContent(prev => ({ ...prev, youtubeChapters: response.data.youtube_chapters }));
        }
        if (response.data.youtube_pinned_comment) {
          setGeneratedContent(prev => ({ ...prev, youtubePinnedComment: response.data.youtube_pinned_comment }));
        }
        if (response.data.youtube_shorts_hooks) {
          setGeneratedContent(prev => ({ ...prev, youtubeShortsHooks: response.data.youtube_shorts_hooks }));
        }
        if (response.data.youtube_srt) {
          setGeneratedContent(prev => ({ ...prev, youtubeSrt: response.data.youtube_srt }));
        }
        if (response.data.youtube_seo_titles) {
          setGeneratedContent(prev => ({ ...prev, youtubeSeoTitles: response.data.youtube_seo_titles }));
        }
      } catch (err) {
        if (!isMounted) return;
        console.error('Error fetching episode:', err);
        setError(err.response?.data?.detail || err.message || 'Failed to load episode');
        toast.error('Failed to load episode');
      } finally {
        if (isMounted) setLoading(false);
      }
    };
    
    const fetchPlanFeatures = async () => {
      setPlanFeaturesLoading(true);
      try {
        const response = await configAPI.getPlanFeatures();
        if (!isMounted) return;
        setPlanFeatures(response.data?.features || {});
      } catch (err) {
        console.error('Failed to fetch plan features:', err);
        // Fallback for pro users
        if (user?.plan === 'pro' || user?.plan === 'unlimited') {
          setPlanFeatures({
            tone_presets: true,
            advanced_instructions: true,
            best_quotes: true,
            seo_titles: true,
            tiktok_captions: true,
            instagram_captions: true,
            youtube_outputs: true,
            zip_export: true,
            regen_limit: 10
          });
        }
      } finally {
        if (isMounted) setPlanFeaturesLoading(false);
      }
    };
    
    // Task 2: Regeneration removed - no longer fetching regen status

    // Reset states on navigation
    setGenerating({});
    setGenerationErrors({});
    
    fetchEpisode();
    fetchPlanFeatures();
    
    const interval = setInterval(async () => {
      if (!isMounted) return;
      try {
        const response = await episodesAPI.getOne(id);
        if (!isMounted) return;
        if (response.data) {
          setEpisode(response.data);
          if (response.data.status === 'completed' || response.data.status === 'failed') {
            clearInterval(interval);
          }
        }
      } catch (err) {
        console.error('Polling error:', err);
        clearInterval(interval);
      }
    }, 5000);

    return () => {
      isMounted = false;
      clearInterval(interval);
    };
  }, [id, user?.plan]);

  // Phase 1: ZIP Export
  const handleZipExport = useCallback(async () => {
    if (!planFeatures?.zip_export) {
      toast.error('ZIP export requires Basic plan or higher');
      return;
    }
    try {
      setGenerating(prev => ({ ...prev, zip: true }));
      const response = await episodesAPI.exportZip(id);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `${episode?.title || 'episode'}-content.zip`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('ZIP downloaded successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to export ZIP');
    } finally {
      setGenerating(prev => ({ ...prev, zip: false }));
    }
  }, [id, episode?.title, planFeatures?.zip_export]);

  // Phase 2: Generate Best Quotes
  const handleGenerateBestQuotes = useCallback(async () => {
    // Prevent double-click
    if (generating.bestQuotes) {
      console.log('[Generate] Already generating quotes, ignoring click');
      return;
    }
    
    // Check feature access
    if (planFeaturesLoading) {
      toast.info('Loading features, please wait...');
      return;
    }
    if (!planFeatures?.best_quotes) {
      toast.error('Best quotes requires Pro plan or higher');
      return;
    }
    
    console.log('[Generate] Starting best quotes generation for episode:', id);
    
    try {
      setGenerating(prev => ({ ...prev, bestQuotes: true }));
      setGenerationErrors(prev => ({ ...prev, bestQuotes: null }));
      
      const response = await episodesAPI.generateBestQuotes(id);
      console.log('[Generate] Best quotes response:', response.status, response.data);
      
      const quotes = response.data?.best_quotes || response.data?.quotes || [];
      if (Array.isArray(quotes) && quotes.length > 0) {
        setGeneratedContent(prev => ({ ...prev, bestQuotes: quotes }));
        toast.success('Best quotes generated!');
      } else {
        setGenerationErrors(prev => ({ ...prev, bestQuotes: 'No quotes could be extracted from this episode' }));
        toast.info('No quotes found in this episode');
      }
    } catch (err) {
      console.error('[Generate] Best quotes error:', err);
      const status = err.response?.status || 'unknown';
      const errorMsg = err.response?.data?.detail || err.message || 'Failed to generate quotes';
      setGenerationErrors(prev => ({ ...prev, bestQuotes: `Error (${status}): ${errorMsg}` }));
      toast.error(`Failed to generate quotes (${status}): ${errorMsg}`);
    } finally {
      setGenerating(prev => ({ ...prev, bestQuotes: false }));
    }
  }, [id, planFeatures?.best_quotes, planFeaturesLoading, generating.bestQuotes]);

  // Phase 2: Generate SEO Titles
  const handleGenerateSeoTitles = useCallback(async () => {
    // Prevent double-click
    if (generating.seoTitles) {
      console.log('[Generate] Already generating titles, ignoring click');
      return;
    }
    
    // Check feature access
    if (planFeaturesLoading) {
      toast.info('Loading features, please wait...');
      return;
    }
    if (!planFeatures?.seo_titles) {
      toast.error('SEO titles requires Pro plan or higher');
      return;
    }
    
    console.log('[Generate] Starting SEO titles generation for episode:', id);
    
    try {
      setGenerating(prev => ({ ...prev, seoTitles: true }));
      setGenerationErrors(prev => ({ ...prev, seoTitles: null }));
      
      const response = await episodesAPI.generateSeoTitles(id);
      console.log('[Generate] SEO titles response:', response.status, response.data);
      
      const titles = response.data?.seo_titles || response.data?.titles || [];
      if (Array.isArray(titles) && titles.length > 0) {
        setGeneratedContent(prev => ({ ...prev, seoTitles: titles }));
        toast.success('SEO titles generated!');
      } else {
        setGenerationErrors(prev => ({ ...prev, seoTitles: 'No titles could be generated' }));
        toast.info('No titles generated');
      }
    } catch (err) {
      console.error('[Generate] SEO titles error:', err);
      const status = err.response?.status || 'unknown';
      const errorMsg = err.response?.data?.detail || err.message || 'Failed to generate SEO titles';
      setGenerationErrors(prev => ({ ...prev, seoTitles: `Error (${status}): ${errorMsg}` }));
      toast.error(`Failed to generate SEO titles (${status}): ${errorMsg}`);
    } finally {
      setGenerating(prev => ({ ...prev, seoTitles: false }));
    }
  }, [id, planFeatures?.seo_titles, planFeaturesLoading, generating.seoTitles]);

  // Phase 2: Generate TikTok Captions
  const handleGenerateTiktokCaptions = useCallback(async () => {
    // Prevent double-click
    if (generating.tiktokCaptions) {
      console.log('[Generate] Already generating TikTok captions, ignoring click');
      return;
    }
    
    // Check feature access
    if (planFeaturesLoading) {
      toast.info('Loading features, please wait...');
      return;
    }
    if (!planFeatures?.tiktok_captions) {
      toast.error('TikTok captions requires Pro plan or higher');
      return;
    }
    
    console.log('[Generate] Starting TikTok captions generation for episode:', id);
    
    try {
      setGenerating(prev => ({ ...prev, tiktokCaptions: true }));
      setGenerationErrors(prev => ({ ...prev, tiktokCaptions: null }));
      
      const response = await episodesAPI.generateTiktokCaptions(id);
      console.log('[Generate] TikTok captions response:', response.status, response.data);
      
      // TikTok captions is an object: { hook, main_caption, hashtags, cta, variations }
      const captionsData = response.data?.tiktok_captions;
      if (captionsData && (captionsData.hook || captionsData.main_caption)) {
        setGeneratedContent(prev => ({ ...prev, tiktokCaptions: captionsData }));
        toast.success('TikTok captions generated!');
      } else {
        setGenerationErrors(prev => ({ ...prev, tiktokCaptions: 'No captions could be generated' }));
        toast.info('No captions generated');
      }
    } catch (err) {
      console.error('[Generate] TikTok captions error:', err);
      const status = err.response?.status || 'unknown';
      const errorMsg = err.response?.data?.detail || err.message || 'Failed to generate TikTok captions';
      setGenerationErrors(prev => ({ ...prev, tiktokCaptions: `Error (${status}): ${errorMsg}` }));
      toast.error(`Failed to generate TikTok captions (${status}): ${errorMsg}`);
    } finally {
      setGenerating(prev => ({ ...prev, tiktokCaptions: false }));
    }
  }, [id, planFeatures?.tiktok_captions, planFeaturesLoading, generating.tiktokCaptions]);

  // Phase 2: Generate Instagram Captions
  const handleGenerateInstagramCaptions = useCallback(async () => {
    // Prevent double-click
    if (generating.instagramCaptions) {
      console.log('[Generate] Already generating Instagram captions, ignoring click');
      return;
    }
    
    // Check feature access
    if (planFeaturesLoading) {
      toast.info('Loading features, please wait...');
      return;
    }
    if (!planFeatures?.instagram_captions) {
      toast.error('Instagram captions requires Pro plan or higher');
      return;
    }
    
    console.log('[Generate] Starting Instagram captions generation for episode:', id);
    
    try {
      setGenerating(prev => ({ ...prev, instagramCaptions: true }));
      setGenerationErrors(prev => ({ ...prev, instagramCaptions: null }));
      
      const response = await episodesAPI.generateInstagramCaptions(id);
      console.log('[Generate] Instagram captions response:', response.status, response.data);
      
      // Instagram captions is an object: { carousel_caption, story_caption, reel_caption, hashtags, bio_link_cta }
      const captionsData = response.data?.instagram_captions;
      if (captionsData && (captionsData.carousel_caption || captionsData.story_caption || captionsData.reel_caption)) {
        setGeneratedContent(prev => ({ ...prev, instagramCaptions: captionsData }));
        toast.success('Instagram captions generated!');
      } else {
        setGenerationErrors(prev => ({ ...prev, instagramCaptions: 'No captions could be generated' }));
        toast.info('No captions generated');
      }
    } catch (err) {
      console.error('[Generate] Instagram captions error:', err);
      const status = err.response?.status || 'unknown';
      const errorMsg = err.response?.data?.detail || err.message || 'Failed to generate Instagram captions';
      setGenerationErrors(prev => ({ ...prev, instagramCaptions: `Error (${status}): ${errorMsg}` }));
      toast.error(`Failed to generate Instagram captions (${status}): ${errorMsg}`);
    } finally {
      setGenerating(prev => ({ ...prev, instagramCaptions: false }));
    }
  }, [id, planFeatures?.instagram_captions, planFeaturesLoading, generating.instagramCaptions]);

  // YouTube Generation Handlers
  const handleGenerateYoutubeDescription = useCallback(async () => {
    if (generating.youtubeDescription) return;
    if (planFeaturesLoading) { toast.info('Loading features...'); return; }
    if (!planFeatures?.youtube_outputs) { toast.error('YouTube outputs require Pro plan or higher'); return; }
    
    try {
      setGenerating(prev => ({ ...prev, youtubeDescription: true }));
      setGenerationErrors(prev => ({ ...prev, youtubeDescription: null }));
      const response = await episodesAPI.generateYoutubeDescription(id);
      if (response.data?.youtube_description) {
        setGeneratedContent(prev => ({ ...prev, youtubeDescription: response.data.youtube_description }));
        toast.success('YouTube description generated!');
      }
    } catch (err) {
      const errorMsg = err.response?.data?.detail || err.message || 'Failed to generate';
      setGenerationErrors(prev => ({ ...prev, youtubeDescription: errorMsg }));
      toast.error(errorMsg);
    } finally {
      setGenerating(prev => ({ ...prev, youtubeDescription: false }));
    }
  }, [id, planFeatures?.youtube_outputs, planFeaturesLoading, generating.youtubeDescription]);

  const handleGenerateYoutubeChapters = useCallback(async () => {
    if (generating.youtubeChapters) return;
    if (planFeaturesLoading) { toast.info('Loading features...'); return; }
    if (!planFeatures?.youtube_outputs) { toast.error('YouTube outputs require Pro plan or higher'); return; }
    
    try {
      setGenerating(prev => ({ ...prev, youtubeChapters: true }));
      setGenerationErrors(prev => ({ ...prev, youtubeChapters: null }));
      const response = await episodesAPI.generateYoutubeChapters(id);
      if (response.data?.youtube_chapters) {
        setGeneratedContent(prev => ({ ...prev, youtubeChapters: response.data.youtube_chapters }));
        toast.success('YouTube chapters generated!');
      }
    } catch (err) {
      const errorMsg = err.response?.data?.detail || err.message || 'Failed to generate';
      setGenerationErrors(prev => ({ ...prev, youtubeChapters: errorMsg }));
      toast.error(errorMsg);
    } finally {
      setGenerating(prev => ({ ...prev, youtubeChapters: false }));
    }
  }, [id, planFeatures?.youtube_outputs, planFeaturesLoading, generating.youtubeChapters]);

  const handleGenerateYoutubePinnedComment = useCallback(async () => {
    if (generating.youtubePinnedComment) return;
    if (planFeaturesLoading) { toast.info('Loading features...'); return; }
    if (!planFeatures?.youtube_outputs) { toast.error('YouTube outputs require Pro plan or higher'); return; }
    
    try {
      setGenerating(prev => ({ ...prev, youtubePinnedComment: true }));
      setGenerationErrors(prev => ({ ...prev, youtubePinnedComment: null }));
      const response = await episodesAPI.generateYoutubePinnedComment(id);
      if (response.data?.youtube_pinned_comment) {
        setGeneratedContent(prev => ({ ...prev, youtubePinnedComment: response.data.youtube_pinned_comment }));
        toast.success('YouTube pinned comment generated!');
      }
    } catch (err) {
      const errorMsg = err.response?.data?.detail || err.message || 'Failed to generate';
      setGenerationErrors(prev => ({ ...prev, youtubePinnedComment: errorMsg }));
      toast.error(errorMsg);
    } finally {
      setGenerating(prev => ({ ...prev, youtubePinnedComment: false }));
    }
  }, [id, planFeatures?.youtube_outputs, planFeaturesLoading, generating.youtubePinnedComment]);

  const handleGenerateYoutubeShortsHooks = useCallback(async () => {
    if (generating.youtubeShortsHooks) return;
    if (planFeaturesLoading) { toast.info('Loading features...'); return; }
    if (!planFeatures?.youtube_outputs) { toast.error('YouTube outputs require Pro plan or higher'); return; }
    
    try {
      setGenerating(prev => ({ ...prev, youtubeShortsHooks: true }));
      setGenerationErrors(prev => ({ ...prev, youtubeShortsHooks: null }));
      const response = await episodesAPI.generateYoutubeShortsHooks(id);
      if (response.data?.youtube_shorts_hooks) {
        setGeneratedContent(prev => ({ ...prev, youtubeShortsHooks: response.data.youtube_shorts_hooks }));
        toast.success('YouTube Shorts hooks generated!');
      }
    } catch (err) {
      const errorMsg = err.response?.data?.detail || err.message || 'Failed to generate';
      setGenerationErrors(prev => ({ ...prev, youtubeShortsHooks: errorMsg }));
      toast.error(errorMsg);
    } finally {
      setGenerating(prev => ({ ...prev, youtubeShortsHooks: false }));
    }
  }, [id, planFeatures?.youtube_outputs, planFeaturesLoading, generating.youtubeShortsHooks]);

  const handleGenerateYoutubeSrt = useCallback(async () => {
    if (generating.youtubeSrt) return;
    if (planFeaturesLoading) { toast.info('Loading features...'); return; }
    if (!planFeatures?.youtube_outputs) { toast.error('YouTube outputs require Pro plan or higher'); return; }
    
    try {
      setGenerating(prev => ({ ...prev, youtubeSrt: true }));
      setGenerationErrors(prev => ({ ...prev, youtubeSrt: null }));
      const response = await episodesAPI.generateYoutubeSrt(id);
      if (response.data?.youtube_srt) {
        setGeneratedContent(prev => ({ ...prev, youtubeSrt: response.data.youtube_srt }));
        toast.success('YouTube SRT generated!');
      }
    } catch (err) {
      const errorMsg = err.response?.data?.detail || err.message || 'Failed to generate';
      setGenerationErrors(prev => ({ ...prev, youtubeSrt: errorMsg }));
      toast.error(errorMsg);
    } finally {
      setGenerating(prev => ({ ...prev, youtubeSrt: false }));
    }
  }, [id, planFeatures?.youtube_outputs, planFeaturesLoading, generating.youtubeSrt]);

  const handleDownloadSrt = useCallback(() => {
    if (!generatedContent.youtubeSrt) {
      toast.error('Generate SRT first');
      return;
    }
    const blob = new Blob([generatedContent.youtubeSrt], { type: 'text/srt' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${episode?.title || 'episode'}.srt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success('SRT file downloaded!');
  }, [generatedContent.youtubeSrt, episode?.title]);

  const handleGenerateYoutubeSeoTitles = useCallback(async () => {
    if (generating.youtubeSeoTitles) return;
    if (planFeaturesLoading) { toast.info('Loading features...'); return; }
    if (!planFeatures?.youtube_outputs) { toast.error('YouTube outputs require Pro plan or higher'); return; }
    
    try {
      setGenerating(prev => ({ ...prev, youtubeSeoTitles: true }));
      setGenerationErrors(prev => ({ ...prev, youtubeSeoTitles: null }));
      const response = await episodesAPI.generateYoutubeSeoTitles(id);
      if (response.data?.youtube_seo_titles) {
        setGeneratedContent(prev => ({ ...prev, youtubeSeoTitles: response.data.youtube_seo_titles }));
        toast.success('YouTube SEO titles generated!');
      }
    } catch (err) {
      const errorMsg = err.response?.data?.detail || err.message || 'Failed to generate';
      setGenerationErrors(prev => ({ ...prev, youtubeSeoTitles: errorMsg }));
      toast.error(errorMsg);
    } finally {
      setGenerating(prev => ({ ...prev, youtubeSeoTitles: false }));
    }
  }, [id, planFeatures?.youtube_outputs, planFeaturesLoading, generating.youtubeSeoTitles]);

  const copyAllYoutubeSeoTitles = useCallback(() => {
    if (!generatedContent.youtubeSeoTitles?.length) return;
    const allTitles = generatedContent.youtubeSeoTitles.join('\n');
    copyToClipboard(allTitles, 'yt-seo-all');
  }, [generatedContent.youtubeSeoTitles]);

  // Task 2: Regeneration has been removed from the product
  // Each asset can only be generated once

  const copyToClipboard = useCallback(async (text, type) => {
    await navigator.clipboard.writeText(text);
    setCopied(type);
    toast.success('Copied to clipboard!');
    setTimeout(() => setCopied(null), 2000);
  }, []);

  // Download as TXT
  const downloadAsTxt = useCallback((content, filename) => {
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${filename}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  }, []);

  // Download as Markdown
  const downloadAsMarkdown = useCallback((content, filename) => {
    const blob = new Blob([content], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${filename}.md`;
    a.click();
    URL.revokeObjectURL(url);
  }, []);

  // Download as DOCX (simple HTML to DOCX conversion)
  const downloadAsDocx = useCallback((content, filename) => {
    const htmlContent = `
      <html xmlns:o='urn:schemas-microsoft-com:office:office' xmlns:w='urn:schemas-microsoft-com:office:word' xmlns='http://www.w3.org/TR/REC-html40'>
      <head><meta charset='utf-8'><title>${filename}</title></head>
      <body style="font-family: Arial, sans-serif; line-height: 1.6;">
        <h1>${episode?.title || filename}</h1>
        ${content.split('\n').map(p => p.trim() ? `<p>${p}</p>` : '').join('')}
      </body>
      </html>
    `;
    const blob = new Blob(['\ufeff', htmlContent], { type: 'application/msword' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${filename}.doc`;
    a.click();
    URL.revokeObjectURL(url);
  }, [episode?.title]);

  // Download as PDF (using browser print)
  const downloadAsPdf = useCallback((content, filename) => {
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html>
      <head>
        <title>${filename}</title>
        <style>
          body { font-family: Arial, sans-serif; line-height: 1.6; padding: 40px; max-width: 800px; margin: 0 auto; }
          h1 { color: #333; border-bottom: 2px solid #7C3AED; padding-bottom: 10px; }
          h2 { color: #555; margin-top: 30px; }
          p { margin: 10px 0; }
          .meta { color: #666; font-size: 14px; margin-bottom: 20px; }
        </style>
      </head>
      <body>
        <h1>${episode?.title || filename}</h1>
        <p class="meta">Generated by EpisodeKit</p>
        ${content.split('\n').map(p => {
          if (p.startsWith('## ')) return `<h2>${p.replace('## ', '')}</h2>`;
          if (p.startsWith('### ')) return `<h3>${p.replace('### ', '')}</h3>`;
          return p.trim() ? `<p>${p}</p>` : '';
        }).join('')}
      </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.onload = () => {
      printWindow.print();
    };
  }, [episode?.title]);

  // Export to Notion (opens Notion with content)
  const exportToNotion = useCallback((content, title) => {
    const notionUrl = `https://www.notion.so/new?title=${encodeURIComponent(title)}`;
    copyToClipboard(content, 'notion');
    toast.success('Content copied! Paste it in Notion after the page opens.');
    window.open(notionUrl, '_blank');
  }, [copyToClipboard]);

  // Export to Google Docs
  const exportToGoogleDocs = useCallback((content) => {
    copyToClipboard(content, 'gdocs');
    toast.success('Content copied! Paste it in Google Docs after it opens.');
    window.open('https://docs.google.com/document/create', '_blank');
  }, [copyToClipboard]);

  // Format show notes for export
  const formatShowNotesForExport = useCallback(() => {
    if (!episode?.show_notes) return '';
    const sn = episode.show_notes;
    let text = `# ${episode.title} - Show Notes\n\n`;
    if (sn.summary) text += `## Summary\n${sn.summary}\n\n`;
    if (sn.introduction) text += `## Introduction\n${sn.introduction}\n\n`;
    if (sn.key_takeaways?.length) {
      text += `## Key Takeaways\n${sn.key_takeaways.map(t => `• ${t}`).join('\n')}\n\n`;
    }
    if (episode.chapters?.length) {
      text += `## Chapters\n${episode.chapters.map(c => `${c.timestamp} - ${c.title}`).join('\n')}\n\n`;
    }
    if (sn.resources_mentioned?.length) {
      text += `## Resources Mentioned\n${sn.resources_mentioned.map(r => `• ${r}`).join('\n')}\n\n`;
    }
    if (sn.conclusion) text += `## Conclusion\n${sn.conclusion}\n`;
    return text;
  }, [episode]);

  // Search functionality for transcript
  const filteredTranscript = useMemo(() => {
    if (!episode?.transcript || !searchQuery.trim()) return null;
    const paragraphs = episode.transcript.split('\n').filter(p => p.trim());
    const matches = paragraphs.filter(p => 
      p.toLowerCase().includes(searchQuery.toLowerCase())
    );
    return matches.length > 0 ? matches : null;
  }, [episode?.transcript, searchQuery]);

  const highlightText = (text, query) => {
    if (!query.trim()) return text;
    const regex = new RegExp(`(${query})`, 'gi');
    const parts = text.split(regex);
    return parts.map((part, i) => 
      regex.test(part) ? <mark key={i} className="bg-yellow-500/30 text-yellow-200 px-0.5 rounded">{part}</mark> : part
    );
  };

  if (loading) {
    return (
      <div className="flex min-h-screen bg-[#050505]">
        <Sidebar />
        <main className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <Loader2 className="w-8 h-8 animate-spin text-primary mx-auto mb-4" />
            <p className="text-neutral-400">Loading episode...</p>
          </div>
        </main>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex min-h-screen bg-[#050505]">
        <Sidebar />
        <main className="flex-1 flex items-center justify-center">
          <div className="text-center max-w-md">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-500/10 flex items-center justify-center">
              <X className="w-8 h-8 text-red-500" />
            </div>
            <h2 className="text-xl font-semibold text-white mb-2">Failed to load episode</h2>
            <p className="text-neutral-400 mb-6">{error}</p>
            <Button onClick={() => navigate('/dashboard')} className="btn-primary">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Dashboard
            </Button>
          </div>
        </main>
      </div>
    );
  }

  if (!episode) {
    return (
      <div className="flex min-h-screen bg-[#050505]">
        <Sidebar />
        <main className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <p className="text-neutral-400 mb-4">Episode not found</p>
            <Button onClick={() => navigate('/dashboard')} className="btn-primary">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Dashboard
            </Button>
          </div>
        </main>
      </div>
    );
  }

  const isProcessing = ['pending', 'transcribing', 'generating'].includes(episode?.status);

  return (
    <div className="flex min-h-screen bg-[#050505]" data-testid="episode-detail-page">
      <Sidebar />
      
      <main className="flex-1 p-8">
        <div className="max-w-5xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <button
              onClick={() => navigate('/dashboard')}
              className="flex items-center gap-2 text-neutral-400 hover:text-white mb-4 transition-colors"
              data-testid="back-btn"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Dashboard
            </button>
            
            <div className="flex items-start justify-between">
              <div>
                <h1 className="text-3xl font-unbounded font-bold mb-2">{episode.title}</h1>
                <div className="flex items-center gap-4 text-neutral-400 text-sm flex-wrap">
                  {episode.duration_minutes && (
                    <span className="flex items-center gap-1">
                      <Clock className="w-4 h-4" />
                      {Math.round(episode.duration_minutes)} minutes
                    </span>
                  )}
                  <span>{new Date(episode.created_at).toLocaleDateString()}</span>
                  <span className="capitalize">{episode.tone} tone</span>
                  
                  {/* Task 1: Language display removed - all content is now English only */}
                </div>
              </div>
              
              {isProcessing && (
                <div className="flex items-center gap-2 text-primary">
                  <Loader2 className="w-5 h-5 animate-spin" />
                  <span className="capitalize">{episode.status}...</span>
                </div>
              )}
            </div>
          </div>

          {/* Processing State */}
          {isProcessing && (
            <motion.div 
              className="card-highlight text-center py-16"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
            >
              <Loader2 className="w-16 h-16 mx-auto mb-4 text-primary animate-spin" />
              <h3 className="text-xl font-semibold mb-2">Processing Your Episode</h3>
              <p className="text-neutral-400">
                {episode.status === 'transcribing' && 'Transcribing audio with AI...'}
                {episode.status === 'generating' && 'Generating content...'}
                {episode.status === 'pending' && 'Waiting to start processing...'}
              </p>
              <p className="text-sm text-neutral-500 mt-2">This may take a few minutes</p>
            </motion.div>
          )}

          {/* Failed State */}
          {episode.status === 'failed' && (
            <motion.div 
              className="card-base border-red-500/50 text-center py-16"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
            >
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-500/10 flex items-center justify-center">
                <FileText className="w-8 h-8 text-red-500" />
              </div>
              <h3 className="text-xl font-semibold mb-2">Processing Failed</h3>
              <p className="text-neutral-400 mb-4">{episode.error_message || 'An error occurred during processing'}</p>
              <Button className="btn-secondary" onClick={() => navigate('/dashboard')}>
                Return to Dashboard
              </Button>
            </motion.div>
          )}

          {/* Completed - Content Tabs */}
          {episode.status === 'completed' && (
            <Tabs defaultValue="transcript" className="space-y-6">
              <TabsList className="bg-transparent border-b border-neutral-800 rounded-none p-0 h-auto flex-wrap">
                <TabsTrigger value="transcript" className="tab-button" data-testid="tab-transcript">
                  <FileText className="w-4 h-4 mr-2" />
                  Transcript
                </TabsTrigger>
                <TabsTrigger value="show-notes" className="tab-button" data-testid="tab-show-notes">
                  <BookOpen className="w-4 h-4 mr-2" />
                  Show Notes
                </TabsTrigger>
                <TabsTrigger value="blog" className="tab-button" data-testid="tab-blog">
                  <FileText className="w-4 h-4 mr-2" />
                  Blog Post
                </TabsTrigger>
                <TabsTrigger value="promo" className="tab-button" data-testid="tab-promo">
                  <Share2 className="w-4 h-4 mr-2" />
                  Promo Snippets
                </TabsTrigger>
                <TabsTrigger value="ai-tools" className="tab-button" data-testid="tab-ai-tools">
                  <Sparkles className="w-4 h-4 mr-2" />
                  AI Tools
                  {(planFeatures?.best_quotes || planFeatures?.seo_titles) && (
                    <Crown className="w-3 h-3 ml-1 text-yellow-500" />
                  )}
                </TabsTrigger>
                <TabsTrigger value="youtube" className="tab-button" data-testid="tab-youtube">
                  <Video className="w-4 h-4 mr-2" />
                  YouTube
                  {planFeatures?.youtube_outputs && (
                    <Crown className="w-3 h-3 ml-1 text-yellow-500" />
                  )}
                </TabsTrigger>
              </TabsList>

              {/* Transcript Tab */}
              <TabsContent value="transcript" className="tab-content">
                <div className="card-base">
                  {episode?.transcript ? (
                    <>
                      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
                        <h3 className="font-semibold">Full Transcript</h3>
                        <div className="flex items-center gap-3">
                          {/* Search */}
                          <div className="relative">
                            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500 pointer-events-none" />
                            <Input
                              placeholder="Search transcript..."
                              value={searchQuery}
                              onChange={(e) => setSearchQuery(e.target.value)}
                              className="input-base pl-11 pr-8 w-48 h-8 text-sm"
                              data-testid="transcript-search"
                            />
                        {searchQuery && (
                          <button
                            onClick={() => setSearchQuery('')}
                            className="absolute right-2 top-1/2 -translate-y-1/2 text-neutral-500 hover:text-white"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                      <ExportButtons 
                        content={episode.transcript} 
                        filename={`${episode.title}-transcript`}
                        type="transcript"
                        copied={copied}
                        onCopy={copyToClipboard}
                        onDownloadTxt={downloadAsTxt}
                        onDownloadDocx={downloadAsDocx}
                        onDownloadPdf={downloadAsPdf}
                      />
                    </div>
                  </div>
                  
                  {/* Search Results */}
                  {searchQuery && (
                    <div className="mb-4 p-3 rounded-lg bg-neutral-900/50 border border-neutral-800">
                      <p className="text-sm text-neutral-400">
                        {filteredTranscript ? (
                          <>{filteredTranscript.length} matches found for &ldquo;{searchQuery}&rdquo;</>
                        ) : (
                          <>No matches found for &ldquo;{searchQuery}&rdquo;</>
                        )}
                      </p>
                    </div>
                  )}
                  
                  <div className="prose prose-invert max-w-none">
                    {searchQuery && filteredTranscript ? (
                      <div className="space-y-3">
                        {filteredTranscript.map((paragraph, i) => (
                          <p 
                            key={i}
                            className="text-neutral-300 leading-relaxed p-2 rounded hover:bg-neutral-800/50 transition-colors cursor-pointer"
                            onClick={() => setHighlightedParagraph(i)}
                          >
                            {highlightText(paragraph, searchQuery)}
                          </p>
                        ))}
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {episode.transcript.split('\n').filter(p => p.trim()).map((paragraph, i) => (
                          <p 
                            key={i}
                            className={`text-neutral-300 leading-relaxed p-2 rounded transition-colors cursor-pointer hover:bg-neutral-800/50 ${
                              highlightedParagraph === i ? 'bg-primary/10 border-l-2 border-primary' : ''
                            }`}
                            onClick={() => setHighlightedParagraph(highlightedParagraph === i ? null : i)}
                          >
                            {paragraph}
                          </p>
                        ))}
                      </div>
                    )}
                  </div>
                  
                  <AdvancedExportButtons 
                    content={episode.transcript} 
                    title={`${episode.title}-transcript`}
                    onExportNotion={exportToNotion}
                    onExportGoogleDocs={exportToGoogleDocs}
                    onDownloadMarkdown={downloadAsMarkdown}
                  />
                    </>
                  ) : (
                    <div className="text-center py-8">
                      <p className="text-neutral-400">No transcript available yet.</p>
                      {episode?.status === 'transcribing' && (
                        <p className="text-sm text-neutral-500 mt-2">Transcription in progress...</p>
                      )}
                    </div>
                  )}
                </div>
              </TabsContent>

              {/* Show Notes Tab */}
              <TabsContent value="show-notes" className="tab-content space-y-6">
                {/* Export Bar */}
                <div className="card-base">
                  <div className="flex items-center justify-between flex-wrap gap-3">
                    <h3 className="font-semibold">Export Show Notes</h3>
                    <ExportButtons 
                      content={formatShowNotesForExport()} 
                      filename={`${episode.title}-show-notes`}
                      type="shownotes"
                      copied={copied}
                      onCopy={copyToClipboard}
                      onDownloadTxt={downloadAsTxt}
                      onDownloadDocx={downloadAsDocx}
                      onDownloadPdf={downloadAsPdf}
                    />
                  </div>
                  <AdvancedExportButtons 
                    content={formatShowNotesForExport()} 
                    title={`${episode.title}-show-notes`}
                    onExportNotion={exportToNotion}
                    onExportGoogleDocs={exportToGoogleDocs}
                    onDownloadMarkdown={downloadAsMarkdown}
                  />
                </div>
                
                {/* Summary */}
                <div className="card-base">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-semibold">Summary</h3>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="btn-ghost"
                      onClick={() => copyToClipboard(episode.show_notes?.summary || '', 'summary')}
                    >
                      {copied === 'summary' ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                    </Button>
                  </div>
                  <p className="text-neutral-300">{episode.show_notes?.summary}</p>
                </div>

                {/* Key Takeaways */}
                {episode.show_notes?.key_takeaways?.length > 0 && (
                  <div className="card-base">
                    <h3 className="font-semibold mb-4">Key Takeaways</h3>
                    <ul className="space-y-2">
                      {episode.show_notes.key_takeaways.map((takeaway, i) => (
                        <li key={i} className="flex items-start gap-2 text-neutral-300">
                          <Check className="w-4 h-4 text-success mt-1 flex-shrink-0" />
                          {takeaway}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Chapters */}
                {episode.chapters?.length > 0 && (
                  <div className="card-base">
                    <h3 className="font-semibold mb-4">Chapters</h3>
                    <div className="space-y-3">
                      {episode.chapters.map((chapter, i) => (
                        <div key={i} className="flex items-start gap-4 p-3 rounded-lg bg-neutral-900/50">
                          <span className="font-mono text-sm text-primary">{chapter.timestamp}</span>
                          <div>
                            <p className="font-medium">{chapter.title}</p>
                            {chapter.description && (
                              <p className="text-sm text-neutral-400">{chapter.description}</p>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Resources */}
                {episode.show_notes?.resources_mentioned?.length > 0 && (
                  <div className="card-base">
                    <h3 className="font-semibold mb-4">Resources Mentioned</h3>
                    <ul className="space-y-2">
                      {episode.show_notes.resources_mentioned.map((resource, i) => (
                        <li key={i} className="text-neutral-300">• {resource}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </TabsContent>

              {/* Blog Post Tab */}
              <TabsContent value="blog" className="tab-content">
                <div className="card-base">
                  <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
                    <div>
                      <h3 className="font-semibold">{episode.blog_post?.meta_title || 'Blog Post'}</h3>
                      {episode.blog_post?.meta_description && (
                        <p className="text-sm text-neutral-500 mt-1">{episode.blog_post.meta_description}</p>
                      )}
                    </div>
                    <ExportButtons 
                      content={episode.blog_post?.content || ''} 
                      filename={`${episode.title}-blog`}
                      type="blog"
                      copied={copied}
                      onCopy={copyToClipboard}
                      onDownloadTxt={downloadAsTxt}
                      onDownloadDocx={downloadAsDocx}
                      onDownloadPdf={downloadAsPdf}
                    />
                  </div>

                  {episode.blog_post?.keywords?.length > 0 && (
                    <div className="flex flex-wrap gap-2 mb-4">
                      {episode.blog_post.keywords.map((keyword, i) => (
                        <span key={i} className="px-2 py-1 rounded bg-primary/10 text-primary text-xs">
                          {keyword}
                        </span>
                      ))}
                    </div>
                  )}

                  <div className="prose prose-invert max-w-none">
                    <div className="whitespace-pre-wrap text-neutral-300 leading-relaxed">
                      {episode.blog_post?.content}
                    </div>
                  </div>
                  
                  <AdvancedExportButtons 
                    content={episode.blog_post?.content || ''} 
                    title={`${episode.title}-blog`}
                    onExportNotion={exportToNotion}
                    onExportGoogleDocs={exportToGoogleDocs}
                    onDownloadMarkdown={downloadAsMarkdown}
                  />
                </div>
              </TabsContent>

              {/* Promo Snippets Tab */}
              <TabsContent value="promo" className="tab-content space-y-6">
                {/* Twitter */}
                <div className="card-base">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-semibold flex items-center gap-2">
                      <span className="w-5 h-5 rounded bg-neutral-800 flex items-center justify-center text-xs">X</span>
                      Twitter/X
                    </h3>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="btn-ghost"
                      onClick={() => copyToClipboard(episode.promo_snippets?.twitter || '', 'twitter')}
                    >
                      {copied === 'twitter' ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                    </Button>
                  </div>
                  <p className="text-neutral-300">{episode.promo_snippets?.twitter}</p>
                  <p className="text-xs text-neutral-500 mt-2">
                    {episode.promo_snippets?.twitter?.length || 0}/280 characters
                  </p>
                </div>

                {/* LinkedIn */}
                <div className="card-base">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-semibold flex items-center gap-2">
                      <span className="w-5 h-5 rounded bg-[#0077B5] flex items-center justify-center text-xs">in</span>
                      LinkedIn
                    </h3>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="btn-ghost"
                      onClick={() => copyToClipboard(episode.promo_snippets?.linkedin || '', 'linkedin')}
                    >
                      {copied === 'linkedin' ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                    </Button>
                  </div>
                  <p className="text-neutral-300 whitespace-pre-wrap">{episode.promo_snippets?.linkedin}</p>
                </div>

                {/* Newsletter */}
                <div className="card-base">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-semibold">Newsletter Blurb</h3>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="btn-ghost"
                      onClick={() => copyToClipboard(episode.promo_snippets?.newsletter || '', 'newsletter')}
                    >
                      {copied === 'newsletter' ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                    </Button>
                  </div>
                  <p className="text-neutral-300 whitespace-pre-wrap">{episode.promo_snippets?.newsletter}</p>
                </div>
              </TabsContent>

              {/* AI Tools Tab - Phase 1, 2, 3 Features */}
              <TabsContent value="ai-tools" className="tab-content space-y-6">
                {/* Export Section - Phase 1 */}
                <div className="card-base">
                  <h3 className="font-semibold mb-4 flex items-center gap-2">
                    <Archive className="w-5 h-5" />
                    Export Options
                  </h3>
                  <div className="flex flex-wrap gap-3">
                    <Button
                      onClick={handleZipExport}
                      disabled={generating.zip || !planFeatures?.zip_export}
                      className="btn-secondary"
                      data-testid="export-zip-btn"
                    >
                      {generating.zip ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
                      Download ZIP
                      {!planFeatures?.zip_export && <span className="ml-2 text-xs opacity-60">(Basic+)</span>}
                    </Button>
                  </div>
                </div>

                {/* Task 2: Regeneration Section Removed */}

                {/* Best Quotes - Phase 2 */}
                <div className="card-base">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-semibold flex items-center gap-2">
                      <Quote className="w-5 h-5" />
                      Best Quotes
                      {!planFeatures?.best_quotes && <ProBadge plan="Pro" />}
                    </h3>
                    {planFeatures?.best_quotes ? (
                      <Button
                        onClick={handleGenerateBestQuotes}
                        disabled={generating.bestQuotes || planFeaturesLoading || !!generatedContent.bestQuotes?.length}
                        className={generatedContent.bestQuotes?.length ? "btn-ghost" : "btn-primary"}
                        data-testid="generate-quotes-btn"
                      >
                        {generating.bestQuotes ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Generating...
                          </>
                        ) : (
                          <>
                            <Sparkles className="w-4 h-4 mr-2" />
                            {generatedContent.bestQuotes?.length ? 'Generated ✓' : 'Generate'}
                          </>
                        )}
                      </Button>
                    ) : (
                      <Link to="/pricing">
                        <Button className="btn-primary" size="sm">
                          <Crown className="w-4 h-4 mr-2" />
                          Upgrade to Pro
                        </Button>
                      </Link>
                    )}
                  </div>
                  
                  {/* Loading state */}
                  {generating.bestQuotes && (
                    <div className="flex items-center justify-center py-8">
                      <div className="text-center">
                        <Loader2 className="w-6 h-6 animate-spin text-primary mx-auto mb-2" />
                        <p className="text-sm text-neutral-400">Extracting best quotes...</p>
                      </div>
                    </div>
                  )}
                  
                  {/* Error state */}
                  {!generating.bestQuotes && generationErrors.bestQuotes && (
                    <div className="p-4 rounded-lg bg-red-500/10 border border-red-500/20">
                      <p className="text-sm text-red-400">{generationErrors.bestQuotes}</p>
                    </div>
                  )}
                  
                  {/* Results - when user has access */}
                  {!generating.bestQuotes && !generationErrors.bestQuotes && generatedContent.bestQuotes?.length > 0 && planFeatures?.best_quotes && (
                    <div className="space-y-3">
                      {generatedContent.bestQuotes.map((quote, i) => (
                        <div key={i} className="p-4 rounded-lg bg-neutral-900/50 border-l-2 border-primary">
                          <p className="text-neutral-200 italic">
                            &ldquo;{typeof quote === 'string' ? quote : quote?.quote || quote?.text || 'Quote unavailable'}&rdquo;
                          </p>
                          {(quote?.context || quote?.speaker) && (
                            <p className="text-xs text-neutral-500 mt-2">{quote.context || quote.speaker}</p>
                          )}
                          <Button
                            variant="ghost"
                            size="sm"
                            className="btn-ghost mt-2"
                            onClick={() => copyToClipboard(typeof quote === 'string' ? quote : quote?.quote || quote?.text || '', `quote-${i}`)}
                          >
                            {copied === `quote-${i}` ? <Check className="w-3 h-3 mr-1" /> : <Copy className="w-3 h-3 mr-1" />}
                            Copy
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {/* LOCKED PREVIEW for Free users */}
                  {!planFeatures?.best_quotes && !planFeaturesLoading && (
                    <div className="relative">
                      <div className="space-y-3 pointer-events-none select-none">
                        {samplePreviewData.bestQuotes.map((quote, i) => (
                          <div key={i} className="p-4 rounded-lg bg-neutral-900/50 border-l-2 border-neutral-700">
                            <p className="text-neutral-400 italic blur-[2px]">
                              &ldquo;{quote.quote}&rdquo;
                            </p>
                            <p className="text-xs text-neutral-600 mt-2 blur-[2px]">— {quote.speaker}</p>
                            <div className="flex items-center gap-1 mt-2 opacity-50">
                              <Lock className="w-3 h-3" />
                              <span className="text-xs text-neutral-500">Copy</span>
                            </div>
                          </div>
                        ))}
                      </div>
                      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#0A0A0A]/50 to-[#0A0A0A]/90 flex items-end justify-center pb-4">
                        <Link to="/pricing">
                          <Button size="sm" className="btn-primary">
                            <Lock className="w-3 h-3 mr-2" />
                            Upgrade to unlock quotes
                          </Button>
                        </Link>
                      </div>
                    </div>
                  )}
                  
                  {/* Default state - only show if user has access but hasn't generated */}
                  {!generating.bestQuotes && !generationErrors.bestQuotes && !generatedContent.bestQuotes?.length && planFeatures?.best_quotes && (
                    <p className="text-sm text-neutral-500">Extract the most shareable quotes from your episode</p>
                  )}
                </div>

                {/* SEO Titles - Phase 2 */}
                <div className="card-base">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-semibold flex items-center gap-2">
                      <FileText className="w-5 h-5" />
                      SEO Title Suggestions
                      {!planFeatures?.seo_titles && <ProBadge plan="Pro" />}
                    </h3>
                    {planFeatures?.seo_titles ? (
                      <Button
                        onClick={handleGenerateSeoTitles}
                        disabled={generating.seoTitles || planFeaturesLoading || !!generatedContent.seoTitles?.length}
                        className={generatedContent.seoTitles?.length ? "btn-ghost" : "btn-primary"}
                        data-testid="generate-seo-btn"
                      >
                        {generating.seoTitles ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Generating...
                          </>
                        ) : (
                          <>
                            <Sparkles className="w-4 h-4 mr-2" />
                            {generatedContent.seoTitles?.length ? 'Generated ✓' : 'Generate'}
                          </>
                        )}
                      </Button>
                    ) : (
                      <Link to="/pricing">
                        <Button className="btn-primary" size="sm">
                          <Crown className="w-4 h-4 mr-2" />
                          Upgrade to Pro
                        </Button>
                      </Link>
                    )}
                  </div>
                  
                  {/* Loading state */}
                  {generating.seoTitles && (
                    <div className="flex items-center justify-center py-8">
                      <div className="text-center">
                        <Loader2 className="w-6 h-6 animate-spin text-primary mx-auto mb-2" />
                        <p className="text-sm text-neutral-400">Generating SEO titles...</p>
                      </div>
                    </div>
                  )}
                  
                  {/* Error state */}
                  {!generating.seoTitles && generationErrors.seoTitles && (
                    <div className="p-4 rounded-lg bg-red-500/10 border border-red-500/20">
                      <p className="text-sm text-red-400">{generationErrors.seoTitles}</p>
                    </div>
                  )}
                  
                  {/* Results - when user has access */}
                  {!generating.seoTitles && !generationErrors.seoTitles && generatedContent.seoTitles?.length > 0 && planFeatures?.seo_titles && (
                    <div className="space-y-2">
                      {generatedContent.seoTitles.map((title, i) => (
                        <div key={i} className="flex items-center justify-between p-3 rounded-lg bg-neutral-900/50">
                          <span className="text-neutral-200">{typeof title === 'string' ? title : title?.title || 'Title unavailable'}</span>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="btn-ghost"
                            onClick={() => copyToClipboard(typeof title === 'string' ? title : title?.title || '', `seo-${i}`)}
                          >
                            {copied === `seo-${i}` ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {/* LOCKED PREVIEW for Free users */}
                  {!planFeatures?.seo_titles && !planFeaturesLoading && (
                    <div className="relative">
                      <div className="space-y-2 pointer-events-none select-none">
                        {samplePreviewData.seoTitles.slice(0, 3).map((title, i) => (
                          <div key={i} className="flex items-center justify-between p-3 rounded-lg bg-neutral-900/50">
                            <span className="text-neutral-400 blur-[2px]">{title}</span>
                            <div className="flex items-center gap-1 opacity-50">
                              <Lock className="w-3 h-3" />
                            </div>
                          </div>
                        ))}
                      </div>
                      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#0A0A0A]/50 to-[#0A0A0A]/90 flex items-end justify-center pb-4">
                        <Link to="/pricing">
                          <Button size="sm" className="btn-primary">
                            <Lock className="w-3 h-3 mr-2" />
                            Upgrade to unlock SEO titles
                          </Button>
                        </Link>
                      </div>
                    </div>
                  )}
                  
                  {/* Default state - only if user has access but hasn't generated */}
                  {!generating.seoTitles && !generationErrors.seoTitles && !generatedContent.seoTitles?.length && planFeatures?.seo_titles && (
                    <p className="text-sm text-neutral-500">Generate SEO-optimized titles for your episode</p>
                  )}
                </div>

                {/* TikTok Captions - Phase 2 */}
                <div className="card-base">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-semibold flex items-center gap-2">
                      <Video className="w-5 h-5" />
                      TikTok Captions
                      {!planFeatures?.tiktok_captions && <ProBadge plan="Pro" />}
                    </h3>
                    {planFeatures?.tiktok_captions ? (
                      <Button
                        onClick={handleGenerateTiktokCaptions}
                        disabled={generating.tiktokCaptions || planFeaturesLoading || !!generatedContent.tiktokCaptions}
                        className="btn-primary"
                        data-testid="generate-tiktok-btn"
                      >
                        {generating.tiktokCaptions ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Generating...
                          </>
                        ) : (
                          <>
                            <Sparkles className="w-4 h-4 mr-2" />
                            {generatedContent.tiktokCaptions?.hook ? 'Generated ✓' : 'Generate'}
                          </>
                        )}
                      </Button>
                    ) : (
                      <Link to="/pricing">
                        <Button className="btn-primary" size="sm">
                          <Crown className="w-4 h-4 mr-2" />
                          Upgrade to Pro
                        </Button>
                      </Link>
                    )}
                  </div>
                  
                  {/* Loading state */}
                  {generating.tiktokCaptions && (
                    <div className="flex items-center justify-center py-8">
                      <div className="text-center">
                        <Loader2 className="w-6 h-6 animate-spin text-primary mx-auto mb-2" />
                        <p className="text-sm text-neutral-400">Generating TikTok captions...</p>
                      </div>
                    </div>
                  )}
                  
                  {/* Error state */}
                  {!generating.tiktokCaptions && generationErrors.tiktokCaptions && (
                    <div className="p-4 rounded-lg bg-red-500/10 border border-red-500/20">
                      <p className="text-sm text-red-400">{generationErrors.tiktokCaptions}</p>
                    </div>
                  )}
                  
                  {/* Results - Structured TikTok data (when user has access) */}
                  {!generating.tiktokCaptions && !generationErrors.tiktokCaptions && generatedContent.tiktokCaptions?.hook && planFeatures?.tiktok_captions && (
                    <div className="space-y-4">
                      {/* Hook */}
                      <div className="p-3 rounded-lg bg-neutral-900/50 border-l-2 border-pink-500">
                        <p className="text-xs text-pink-400 mb-1 font-medium">Hook (First Line)</p>
                        <p className="text-neutral-200">{generatedContent.tiktokCaptions.hook}</p>
                        <Button variant="ghost" size="sm" className="btn-ghost mt-2"
                          onClick={() => copyToClipboard(generatedContent.tiktokCaptions.hook, 'tiktok-hook')}>
                          {copied === 'tiktok-hook' ? <Check className="w-3 h-3 mr-1" /> : <Copy className="w-3 h-3 mr-1" />}
                          Copy
                        </Button>
                      </div>
                      
                      {/* Main Caption */}
                      {generatedContent.tiktokCaptions.main_caption && (
                        <div className="p-3 rounded-lg bg-neutral-900/50">
                          <p className="text-xs text-neutral-500 mb-1 font-medium">Main Caption</p>
                          <p className="text-neutral-200 text-sm">{generatedContent.tiktokCaptions.main_caption}</p>
                          <Button variant="ghost" size="sm" className="btn-ghost mt-2"
                            onClick={() => copyToClipboard(generatedContent.tiktokCaptions.main_caption, 'tiktok-main')}>
                            {copied === 'tiktok-main' ? <Check className="w-3 h-3 mr-1" /> : <Copy className="w-3 h-3 mr-1" />}
                            Copy
                          </Button>
                        </div>
                      )}
                      
                      {/* Hashtags */}
                      {generatedContent.tiktokCaptions.hashtags?.length > 0 && (
                        <div className="p-3 rounded-lg bg-neutral-900/50">
                          <p className="text-xs text-neutral-500 mb-2 font-medium">Hashtags</p>
                          <p className="text-sm text-blue-400">{generatedContent.tiktokCaptions.hashtags.join(' ')}</p>
                          <Button variant="ghost" size="sm" className="btn-ghost mt-2"
                            onClick={() => copyToClipboard(generatedContent.tiktokCaptions.hashtags.join(' '), 'tiktok-hashtags')}>
                            {copied === 'tiktok-hashtags' ? <Check className="w-3 h-3 mr-1" /> : <Copy className="w-3 h-3 mr-1" />}
                            Copy Hashtags
                          </Button>
                        </div>
                      )}
                      
                      {/* CTA */}
                      {generatedContent.tiktokCaptions.cta && (
                        <div className="p-3 rounded-lg bg-neutral-900/50">
                          <p className="text-xs text-neutral-500 mb-1 font-medium">Call to Action</p>
                          <p className="text-neutral-200 text-sm">{generatedContent.tiktokCaptions.cta}</p>
                        </div>
                      )}
                      
                      {/* Copy Full Caption */}
                      <Button variant="outline" className="w-full btn-secondary"
                        onClick={() => copyToClipboard(
                          `${generatedContent.tiktokCaptions.hook}\n\n${generatedContent.tiktokCaptions.main_caption}\n\n${generatedContent.tiktokCaptions.hashtags?.join(' ') || ''}\n\n${generatedContent.tiktokCaptions.cta || ''}`,
                          'tiktok-full'
                        )}>
                        {copied === 'tiktok-full' ? <Check className="w-4 h-4 mr-2" /> : <Copy className="w-4 h-4 mr-2" />}
                        Copy Full Caption
                      </Button>
                    </div>
                  )}
                  
                  {/* LOCKED PREVIEW for Free users */}
                  {!planFeatures?.tiktok_captions && !planFeaturesLoading && (
                    <div className="relative">
                      <div className="space-y-3 pointer-events-none select-none">
                        <div className="p-3 rounded-lg bg-neutral-900/50 border-l-2 border-neutral-700">
                          <p className="text-xs text-pink-400/50 mb-1 font-medium">Hook (First Line)</p>
                          <p className="text-neutral-400 blur-[2px]">{samplePreviewData.tiktokCaption.hook}</p>
                        </div>
                        <div className="p-3 rounded-lg bg-neutral-900/50">
                          <p className="text-xs text-neutral-500 mb-1 font-medium">Main Caption</p>
                          <p className="text-neutral-400 text-sm blur-[2px]">{samplePreviewData.tiktokCaption.caption}</p>
                        </div>
                      </div>
                      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#0A0A0A]/50 to-[#0A0A0A]/90 flex items-end justify-center pb-4">
                        <Link to="/pricing">
                          <Button size="sm" className="btn-primary">
                            <Lock className="w-3 h-3 mr-2" />
                            Upgrade to unlock TikTok captions
                          </Button>
                        </Link>
                      </div>
                    </div>
                  )}
                  
                  {/* Default state - only if user has access but hasn't generated */}
                  {!generating.tiktokCaptions && !generationErrors.tiktokCaptions && !generatedContent.tiktokCaptions?.hook && planFeatures?.tiktok_captions && (
                    <p className="text-sm text-neutral-500">Generate engaging TikTok captions with hooks, hashtags, and CTAs</p>
                  )}
                </div>

                {/* Instagram Captions - Phase 2 */}
                <div className="card-base">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-semibold flex items-center gap-2">
                      <Instagram className="w-5 h-5" />
                      Instagram Captions
                      {!planFeatures?.instagram_captions && <ProBadge plan="Pro" />}
                    </h3>
                    {planFeatures?.instagram_captions ? (
                      <Button
                        onClick={handleGenerateInstagramCaptions}
                        disabled={generating.instagramCaptions || planFeaturesLoading || !!generatedContent.instagramCaptions}
                        className="btn-primary"
                        data-testid="generate-instagram-btn"
                      >
                        {generating.instagramCaptions ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Generating...
                          </>
                        ) : (
                          <>
                            <Sparkles className="w-4 h-4 mr-2" />
                            {generatedContent.instagramCaptions?.carousel_caption ? 'Generated ✓' : 'Generate'}
                          </>
                        )}
                      </Button>
                    ) : (
                      <Link to="/pricing">
                        <Button className="btn-primary" size="sm">
                          <Crown className="w-4 h-4 mr-2" />
                          Upgrade to Pro
                        </Button>
                      </Link>
                    )}
                  </div>
                  
                  {/* Loading state */}
                  {generating.instagramCaptions && (
                    <div className="flex items-center justify-center py-8">
                      <div className="text-center">
                        <Loader2 className="w-6 h-6 animate-spin text-primary mx-auto mb-2" />
                        <p className="text-sm text-neutral-400">Generating Instagram captions...</p>
                      </div>
                    </div>
                  )}
                  
                  {/* Error state */}
                  {!generating.instagramCaptions && generationErrors.instagramCaptions && (
                    <div className="p-4 rounded-lg bg-red-500/10 border border-red-500/20">
                      <p className="text-sm text-red-400">{generationErrors.instagramCaptions}</p>
                    </div>
                  )}
                  
                  {/* Results - Structured Instagram data (when user has access) */}
                  {!generating.instagramCaptions && !generationErrors.instagramCaptions && generatedContent.instagramCaptions?.carousel_caption && planFeatures?.instagram_captions && (
                    <div className="space-y-4">
                      {/* Carousel Caption */}
                      <div className="p-3 rounded-lg bg-neutral-900/50 border-l-2 border-purple-500">
                        <p className="text-xs text-purple-400 mb-1 font-medium">Carousel Post</p>
                        <p className="text-neutral-200 text-sm whitespace-pre-wrap">{generatedContent.instagramCaptions.carousel_caption}</p>
                        <Button variant="ghost" size="sm" className="btn-ghost mt-2"
                          onClick={() => copyToClipboard(generatedContent.instagramCaptions.carousel_caption, 'ig-carousel')}>
                          {copied === 'ig-carousel' ? <Check className="w-3 h-3 mr-1" /> : <Copy className="w-3 h-3 mr-1" />}
                          Copy
                        </Button>
                      </div>
                      
                      {/* Story Caption */}
                      {generatedContent.instagramCaptions.story_caption && (
                        <div className="p-3 rounded-lg bg-neutral-900/50">
                          <p className="text-xs text-neutral-500 mb-1 font-medium">Story</p>
                          <p className="text-neutral-200 text-sm">{generatedContent.instagramCaptions.story_caption}</p>
                          <Button variant="ghost" size="sm" className="btn-ghost mt-2"
                            onClick={() => copyToClipboard(generatedContent.instagramCaptions.story_caption, 'ig-story')}>
                            {copied === 'ig-story' ? <Check className="w-3 h-3 mr-1" /> : <Copy className="w-3 h-3 mr-1" />}
                            Copy
                          </Button>
                        </div>
                      )}
                      
                      {/* Reel Caption */}
                      {generatedContent.instagramCaptions.reel_caption && (
                        <div className="p-3 rounded-lg bg-neutral-900/50">
                          <p className="text-xs text-neutral-500 mb-1 font-medium">Reel</p>
                          <p className="text-neutral-200 text-sm">{generatedContent.instagramCaptions.reel_caption}</p>
                          <Button variant="ghost" size="sm" className="btn-ghost mt-2"
                            onClick={() => copyToClipboard(generatedContent.instagramCaptions.reel_caption, 'ig-reel')}>
                            {copied === 'ig-reel' ? <Check className="w-3 h-3 mr-1" /> : <Copy className="w-3 h-3 mr-1" />}
                            Copy
                          </Button>
                        </div>
                      )}
                      
                      {/* Hashtags */}
                      {generatedContent.instagramCaptions.hashtags?.length > 0 && (
                        <div className="p-3 rounded-lg bg-neutral-900/50">
                          <p className="text-xs text-neutral-500 mb-2 font-medium">Hashtags</p>
                          <p className="text-sm text-blue-400">{generatedContent.instagramCaptions.hashtags.join(' ')}</p>
                          <Button variant="ghost" size="sm" className="btn-ghost mt-2"
                            onClick={() => copyToClipboard(generatedContent.instagramCaptions.hashtags.join(' '), 'ig-hashtags')}>
                            {copied === 'ig-hashtags' ? <Check className="w-3 h-3 mr-1" /> : <Copy className="w-3 h-3 mr-1" />}
                            Copy Hashtags
                          </Button>
                        </div>
                      )}
                      
                      {/* Bio Link CTA */}
                      {generatedContent.instagramCaptions.bio_link_cta && (
                        <div className="p-3 rounded-lg bg-neutral-900/50">
                          <p className="text-xs text-neutral-500 mb-1 font-medium">Bio Link CTA</p>
                          <p className="text-neutral-200 text-sm">{generatedContent.instagramCaptions.bio_link_cta}</p>
                        </div>
                      )}
                    </div>
                  )}
                  
                  {/* LOCKED PREVIEW for Free users */}
                  {!planFeatures?.instagram_captions && !planFeaturesLoading && (
                    <div className="relative">
                      <div className="space-y-3 pointer-events-none select-none">
                        <div className="p-3 rounded-lg bg-neutral-900/50 border-l-2 border-neutral-700">
                          <p className="text-xs text-purple-400/50 mb-1 font-medium">Carousel Post</p>
                          <p className="text-neutral-400 text-sm blur-[2px]">{samplePreviewData.instagramCaptions.carousel_caption}</p>
                        </div>
                        <div className="p-3 rounded-lg bg-neutral-900/50">
                          <p className="text-xs text-neutral-500 mb-1 font-medium">Story</p>
                          <p className="text-neutral-400 text-sm blur-[2px]">{samplePreviewData.instagramCaptions.story_caption}</p>
                        </div>
                      </div>
                      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#0A0A0A]/50 to-[#0A0A0A]/90 flex items-end justify-center pb-4">
                        <Link to="/pricing">
                          <Button size="sm" className="btn-primary">
                            <Lock className="w-3 h-3 mr-2" />
                            Upgrade to unlock Instagram captions
                          </Button>
                        </Link>
                      </div>
                    </div>
                  )}
                  
                  {/* Default state - only if user has access but hasn't generated */}
                  {!generating.instagramCaptions && !generationErrors.instagramCaptions && !generatedContent.instagramCaptions?.carousel_caption && planFeatures?.instagram_captions && (
                    <p className="text-sm text-neutral-500">Generate Instagram captions for carousels, stories, and reels</p>
                  )}
                </div>
              </TabsContent>

              {/* YouTube Tab */}
              <TabsContent value="youtube" className="tab-content space-y-6">
                {/* YouTube Description */}
                <div className="card-base">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-semibold flex items-center gap-2">
                      <FileText className="w-5 h-5" />
                      YouTube Description
                      {!planFeatures?.youtube_outputs && <ProBadge plan="Pro" />}
                    </h3>
                    {planFeatures?.youtube_outputs ? (
                      <Button
                        onClick={handleGenerateYoutubeDescription}
                        disabled={generating.youtubeDescription || planFeaturesLoading || !!generatedContent.youtubeDescription}
                        className="btn-primary"
                        data-testid="generate-yt-desc-btn"
                      >
                        {generating.youtubeDescription ? (
                          <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Generating...</>
                        ) : (
                          <><Sparkles className="w-4 h-4 mr-2" />{generatedContent.youtubeDescription ? 'Generated ✓' : 'Generate'}</>
                        )}
                      </Button>
                    ) : (
                      <Link to="/pricing"><Button className="btn-primary" size="sm"><Crown className="w-4 h-4 mr-2" />Upgrade to Pro</Button></Link>
                    )}
                  </div>
                  
                  {generating.youtubeDescription && (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="w-6 h-6 animate-spin text-primary" />
                    </div>
                  )}
                  
                  {generationErrors.youtubeDescription && (
                    <div className="p-4 rounded-lg bg-red-500/10 border border-red-500/20">
                      <p className="text-sm text-red-400">{generationErrors.youtubeDescription}</p>
                    </div>
                  )}
                  
                  {generatedContent.youtubeDescription && !generating.youtubeDescription && (
                    <div className="space-y-3">
                      <pre className="whitespace-pre-wrap text-sm text-neutral-300 bg-neutral-900/50 p-4 rounded-lg max-h-96 overflow-y-auto">
                        {generatedContent.youtubeDescription}
                      </pre>
                      <Button variant="ghost" size="sm" className="btn-ghost" onClick={() => copyToClipboard(generatedContent.youtubeDescription, 'yt-desc')}>
                        {copied === 'yt-desc' ? <Check className="w-3 h-3 mr-1" /> : <Copy className="w-3 h-3 mr-1" />}Copy
                      </Button>
                    </div>
                  )}
                  
                  {!planFeatures?.youtube_outputs && !planFeaturesLoading && (
                    <div className="relative">
                      <div className="pointer-events-none select-none">
                        <pre className="whitespace-pre-wrap text-sm text-neutral-400 bg-neutral-900/50 p-4 rounded-lg blur-[2px]">
                          🎯 Hook: This episode changed everything I thought I knew about...{'\n\n'}
                          In this episode, we dive deep into the fascinating world of...{'\n\n'}
                          📌 Key Takeaways:{'\n'}• First major insight from the conversation{'\n'}• Second breakthrough moment...
                        </pre>
                        <div className="flex items-center gap-1 mt-2 opacity-50">
                          <Lock className="w-3 h-3" /><span className="text-xs text-neutral-500">Copy</span>
                        </div>
                      </div>
                      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#0A0A0A]/50 to-[#0A0A0A]/90 flex items-end justify-center pb-4">
                        <Link to="/pricing"><Button size="sm" className="btn-primary"><Lock className="w-3 h-3 mr-2" />Upgrade to unlock</Button></Link>
                      </div>
                    </div>
                  )}
                  
                  {!generating.youtubeDescription && !generationErrors.youtubeDescription && !generatedContent.youtubeDescription && planFeatures?.youtube_outputs && (
                    <p className="text-sm text-neutral-500">Generate SEO-optimized YouTube video description</p>
                  )}
                </div>

                {/* YouTube Chapters */}
                <div className="card-base">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-semibold flex items-center gap-2">
                      <Clock className="w-5 h-5" />
                      YouTube Chapters
                      {!planFeatures?.youtube_outputs && <ProBadge plan="Pro" />}
                    </h3>
                    {planFeatures?.youtube_outputs ? (
                      <Button
                        onClick={handleGenerateYoutubeChapters}
                        disabled={generating.youtubeChapters || planFeaturesLoading || !!generatedContent.youtubeChapters}
                        className="btn-primary"
                        data-testid="generate-yt-chapters-btn"
                      >
                        {generating.youtubeChapters ? (
                          <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Generating...</>
                        ) : (
                          <><Sparkles className="w-4 h-4 mr-2" />{generatedContent.youtubeChapters ? 'Generated ✓' : 'Generate'}</>
                        )}
                      </Button>
                    ) : (
                      <Link to="/pricing"><Button className="btn-primary" size="sm"><Crown className="w-4 h-4 mr-2" />Upgrade to Pro</Button></Link>
                    )}
                  </div>
                  
                  {generating.youtubeChapters && (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="w-6 h-6 animate-spin text-primary" />
                    </div>
                  )}
                  
                  {generatedContent.youtubeChapters && !generating.youtubeChapters && (
                    <div className="space-y-3">
                      <pre className="whitespace-pre-wrap text-sm text-neutral-300 bg-neutral-900/50 p-4 rounded-lg font-mono">
                        {generatedContent.youtubeChapters}
                      </pre>
                      <Button variant="ghost" size="sm" className="btn-ghost" onClick={() => copyToClipboard(generatedContent.youtubeChapters, 'yt-chapters')}>
                        {copied === 'yt-chapters' ? <Check className="w-3 h-3 mr-1" /> : <Copy className="w-3 h-3 mr-1" />}Copy
                      </Button>
                    </div>
                  )}
                  
                  {!planFeatures?.youtube_outputs && !planFeaturesLoading && (
                    <div className="relative">
                      <div className="pointer-events-none select-none">
                        <pre className="whitespace-pre-wrap text-sm text-neutral-400 bg-neutral-900/50 p-4 rounded-lg font-mono blur-[2px]">
                          00:00 Intro{'\n'}03:25 The main topic begins{'\n'}08:40 Key insight revealed{'\n'}15:20 Deep dive into details...
                        </pre>
                        <div className="flex items-center gap-1 mt-2 opacity-50">
                          <Lock className="w-3 h-3" /><span className="text-xs text-neutral-500">Copy</span>
                        </div>
                      </div>
                      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#0A0A0A]/50 to-[#0A0A0A]/90 flex items-end justify-center pb-4">
                        <Link to="/pricing"><Button size="sm" className="btn-primary"><Lock className="w-3 h-3 mr-2" />Upgrade to unlock</Button></Link>
                      </div>
                    </div>
                  )}
                  
                  {!generating.youtubeChapters && !generatedContent.youtubeChapters && planFeatures?.youtube_outputs && (
                    <p className="text-sm text-neutral-500">Generate YouTube-formatted chapter timestamps</p>
                  )}
                </div>

                {/* YouTube Pinned Comment */}
                <div className="card-base">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-semibold flex items-center gap-2">
                      <Quote className="w-5 h-5" />
                      Pinned Comment
                      {!planFeatures?.youtube_outputs && <ProBadge plan="Pro" />}
                    </h3>
                    {planFeatures?.youtube_outputs ? (
                      <Button
                        onClick={handleGenerateYoutubePinnedComment}
                        disabled={generating.youtubePinnedComment || planFeaturesLoading || !!generatedContent.youtubePinnedComment}
                        className="btn-primary"
                        data-testid="generate-yt-pinned-btn"
                      >
                        {generating.youtubePinnedComment ? (
                          <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Generating...</>
                        ) : (
                          <><Sparkles className="w-4 h-4 mr-2" />{generatedContent.youtubePinnedComment ? 'Generated ✓' : 'Generate'}</>
                        )}
                      </Button>
                    ) : (
                      <Link to="/pricing"><Button className="btn-primary" size="sm"><Crown className="w-4 h-4 mr-2" />Upgrade to Pro</Button></Link>
                    )}
                  </div>
                  
                  {generatedContent.youtubePinnedComment && !generating.youtubePinnedComment && (
                    <div className="space-y-3">
                      <p className="text-neutral-300 bg-neutral-900/50 p-4 rounded-lg">{generatedContent.youtubePinnedComment}</p>
                      <Button variant="ghost" size="sm" className="btn-ghost" onClick={() => copyToClipboard(generatedContent.youtubePinnedComment, 'yt-pinned')}>
                        {copied === 'yt-pinned' ? <Check className="w-3 h-3 mr-1" /> : <Copy className="w-3 h-3 mr-1" />}Copy
                      </Button>
                    </div>
                  )}
                  
                  {!planFeatures?.youtube_outputs && !planFeaturesLoading && (
                    <div className="relative">
                      <div className="pointer-events-none select-none">
                        <p className="text-neutral-400 bg-neutral-900/50 p-4 rounded-lg blur-[2px]">
                          What was your biggest takeaway from this episode? Drop a comment below! 👇 Don't forget to subscribe for more...
                        </p>
                        <div className="flex items-center gap-1 mt-2 opacity-50">
                          <Lock className="w-3 h-3" /><span className="text-xs text-neutral-500">Copy</span>
                        </div>
                      </div>
                      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#0A0A0A]/50 to-[#0A0A0A]/90 flex items-end justify-center pb-4">
                        <Link to="/pricing"><Button size="sm" className="btn-primary"><Lock className="w-3 h-3 mr-2" />Upgrade to unlock</Button></Link>
                      </div>
                    </div>
                  )}
                  
                  {!generating.youtubePinnedComment && !generatedContent.youtubePinnedComment && planFeatures?.youtube_outputs && (
                    <p className="text-sm text-neutral-500">Generate an engaging pinned comment for your video</p>
                  )}
                </div>

                {/* YouTube Shorts Hooks */}
                <div className="card-base">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-semibold flex items-center gap-2">
                      <Video className="w-5 h-5" />
                      Shorts Hooks
                      {!planFeatures?.youtube_outputs && <ProBadge plan="Pro" />}
                    </h3>
                    {planFeatures?.youtube_outputs ? (
                      <Button
                        onClick={handleGenerateYoutubeShortsHooks}
                        disabled={generating.youtubeShortsHooks || planFeaturesLoading || !!generatedContent.youtubeShortsHooks}
                        className="btn-primary"
                        data-testid="generate-yt-shorts-btn"
                      >
                        {generating.youtubeShortsHooks ? (
                          <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Generating...</>
                        ) : (
                          <><Sparkles className="w-4 h-4 mr-2" />{generatedContent.youtubeShortsHooks?.length ? 'Generated ✓' : 'Generate'}</>
                        )}
                      </Button>
                    ) : (
                      <Link to="/pricing"><Button className="btn-primary" size="sm"><Crown className="w-4 h-4 mr-2" />Upgrade to Pro</Button></Link>
                    )}
                  </div>
                  
                  {generatedContent.youtubeShortsHooks?.length > 0 && !generating.youtubeShortsHooks && (
                    <div className="space-y-2">
                      {generatedContent.youtubeShortsHooks.map((hook, i) => (
                        <div key={i} className="flex items-center justify-between p-3 rounded-lg bg-neutral-900/50">
                          <span className="text-neutral-300 text-sm">{hook}</span>
                          <Button variant="ghost" size="sm" className="btn-ghost" onClick={() => copyToClipboard(hook, `yt-hook-${i}`)}>
                            {copied === `yt-hook-${i}` ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {!planFeatures?.youtube_outputs && !planFeaturesLoading && (
                    <div className="relative">
                      <div className="pointer-events-none select-none space-y-2">
                        {['This changed everything...', 'You won\'t believe what happened next', 'The secret nobody talks about'].map((hook, i) => (
                          <div key={i} className="flex items-center justify-between p-3 rounded-lg bg-neutral-900/50">
                            <span className="text-neutral-400 text-sm blur-[2px]">{hook}</span>
                            <div className="opacity-50"><Lock className="w-3 h-3" /></div>
                          </div>
                        ))}
                      </div>
                      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#0A0A0A]/50 to-[#0A0A0A]/90 flex items-end justify-center pb-4">
                        <Link to="/pricing"><Button size="sm" className="btn-primary"><Lock className="w-3 h-3 mr-2" />Upgrade to unlock</Button></Link>
                      </div>
                    </div>
                  )}
                  
                  {!generating.youtubeShortsHooks && !generatedContent.youtubeShortsHooks?.length && planFeatures?.youtube_outputs && (
                    <p className="text-sm text-neutral-500">Generate attention-grabbing hooks for YouTube Shorts</p>
                  )}
                </div>

                {/* YouTube SRT Subtitles */}
                <div className="card-base">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-semibold flex items-center gap-2">
                      <FileText className="w-5 h-5" />
                      Subtitles (SRT)
                      {!planFeatures?.youtube_outputs && <ProBadge plan="Pro" />}
                    </h3>
                    {planFeatures?.youtube_outputs ? (
                      <div className="flex gap-2">
                        <Button
                          onClick={handleGenerateYoutubeSrt}
                          disabled={generating.youtubeSrt || planFeaturesLoading || !!generatedContent.youtubeSrt}
                          className="btn-primary"
                          data-testid="generate-yt-srt-btn"
                        >
                          {generating.youtubeSrt ? (
                            <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Generating...</>
                          ) : (
                            <><Sparkles className="w-4 h-4 mr-2" />{generatedContent.youtubeSrt ? 'Generated ✓' : 'Generate'}</>
                          )}
                        </Button>
                        {generatedContent.youtubeSrt && (
                          <Button onClick={handleDownloadSrt} variant="outline" className="btn-ghost" data-testid="download-srt-btn">
                            <Download className="w-4 h-4 mr-2" />Download .srt
                          </Button>
                        )}
                      </div>
                    ) : (
                      <Link to="/pricing"><Button className="btn-primary" size="sm"><Crown className="w-4 h-4 mr-2" />Upgrade to Pro</Button></Link>
                    )}
                  </div>
                  
                  {generatedContent.youtubeSrt && !generating.youtubeSrt && (
                    <div className="space-y-3">
                      <pre className="whitespace-pre-wrap text-xs text-neutral-300 bg-neutral-900/50 p-4 rounded-lg max-h-64 overflow-y-auto font-mono">
                        {generatedContent.youtubeSrt.slice(0, 1000)}...
                      </pre>
                      <div className="flex gap-2">
                        <Button variant="ghost" size="sm" className="btn-ghost" onClick={() => copyToClipboard(generatedContent.youtubeSrt, 'yt-srt')}>
                          {copied === 'yt-srt' ? <Check className="w-3 h-3 mr-1" /> : <Copy className="w-3 h-3 mr-1" />}Copy All
                        </Button>
                      </div>
                    </div>
                  )}
                  
                  {!planFeatures?.youtube_outputs && !planFeaturesLoading && (
                    <div className="relative">
                      <div className="pointer-events-none select-none">
                        <pre className="whitespace-pre-wrap text-xs text-neutral-400 bg-neutral-900/50 p-4 rounded-lg font-mono blur-[2px]">
                          {`1\n00:00:00,000 --> 00:00:05,000\nWelcome to today's episode...\n\n2\n00:00:05,000 --> 00:00:10,000\nWe're going to dive deep into...`}
                        </pre>
                        <div className="flex items-center gap-1 mt-2 opacity-50">
                          <Lock className="w-3 h-3" /><span className="text-xs text-neutral-500">Download</span>
                        </div>
                      </div>
                      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#0A0A0A]/50 to-[#0A0A0A]/90 flex items-end justify-center pb-4">
                        <Link to="/pricing"><Button size="sm" className="btn-primary"><Lock className="w-3 h-3 mr-2" />Upgrade to unlock</Button></Link>
                      </div>
                    </div>
                  )}
                  
                  {!generating.youtubeSrt && !generatedContent.youtubeSrt && planFeatures?.youtube_outputs && (
                    <p className="text-sm text-neutral-500">Generate SRT subtitle file from your transcript</p>
                  )}
                </div>

                {/* YouTube SEO Titles */}
                <div className="card-base">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-semibold flex items-center gap-2">
                      <Search className="w-5 h-5" />
                      YouTube SEO Titles
                      {!planFeatures?.youtube_outputs && <ProBadge plan="Pro" />}
                    </h3>
                    {planFeatures?.youtube_outputs ? (
                      <div className="flex gap-2">
                        <Button
                          onClick={handleGenerateYoutubeSeoTitles}
                          disabled={generating.youtubeSeoTitles || planFeaturesLoading || !!generatedContent.youtubeSeoTitles?.length}
                          className="btn-primary"
                          data-testid="generate-yt-seo-titles-btn"
                        >
                          {generating.youtubeSeoTitles ? (
                            <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Generating...</>
                          ) : (
                            <><Sparkles className="w-4 h-4 mr-2" />{generatedContent.youtubeSeoTitles?.length ? 'Generated ✓' : 'Generate'}</>
                          )}
                        </Button>
                        {generatedContent.youtubeSeoTitles?.length > 0 && (
                          <Button onClick={copyAllYoutubeSeoTitles} variant="outline" className="btn-ghost" data-testid="copy-all-yt-seo-titles-btn">
                            {copied === 'yt-seo-all' ? <Check className="w-4 h-4 mr-2" /> : <Copy className="w-4 h-4 mr-2" />}Copy All
                          </Button>
                        )}
                      </div>
                    ) : (
                      <Link to="/pricing"><Button className="btn-primary" size="sm"><Crown className="w-4 h-4 mr-2" />Upgrade to Pro</Button></Link>
                    )}
                  </div>
                  
                  {generating.youtubeSeoTitles && (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="w-6 h-6 animate-spin text-primary" />
                    </div>
                  )}
                  
                  {generationErrors.youtubeSeoTitles && (
                    <div className="p-4 rounded-lg bg-red-500/10 border border-red-500/20">
                      <p className="text-sm text-red-400">{generationErrors.youtubeSeoTitles}</p>
                    </div>
                  )}
                  
                  {generatedContent.youtubeSeoTitles?.length > 0 && !generating.youtubeSeoTitles && (
                    <div className="space-y-2">
                      {generatedContent.youtubeSeoTitles.map((title, i) => (
                        <div key={i} className="flex items-center justify-between p-3 rounded-lg bg-neutral-900/50 group">
                          <span className="text-neutral-300 text-sm flex-1 mr-3">{title}</span>
                          <Button variant="ghost" size="sm" className="btn-ghost opacity-60 group-hover:opacity-100" onClick={() => copyToClipboard(title, `yt-seo-${i}`)}>
                            {copied === `yt-seo-${i}` ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {!planFeatures?.youtube_outputs && !planFeaturesLoading && (
                    <div className="relative">
                      <div className="pointer-events-none select-none space-y-2">
                        {/* Show first 2 titles unblurred, rest blurred */}
                        <div className="flex items-center justify-between p-3 rounded-lg bg-neutral-900/50">
                          <span className="text-neutral-300 text-sm">Why Boring Businesses Make Millions</span>
                          <div className="opacity-50"><Lock className="w-3 h-3" /></div>
                        </div>
                        <div className="flex items-center justify-between p-3 rounded-lg bg-neutral-900/50">
                          <span className="text-neutral-300 text-sm">The Startup Mistake Everyone Makes</span>
                          <div className="opacity-50"><Lock className="w-3 h-3" /></div>
                        </div>
                        {['How to Find Profitable Ideas Nobody Wants', 'Stop Chasing Trends — Do This Instead', '7 Boring Problems That Turn Into Big Businesses'].map((title, i) => (
                          <div key={i} className="flex items-center justify-between p-3 rounded-lg bg-neutral-900/50">
                            <span className="text-neutral-400 text-sm blur-[2px]">{title}</span>
                            <div className="opacity-50"><Lock className="w-3 h-3" /></div>
                          </div>
                        ))}
                      </div>
                      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#0A0A0A]/50 to-[#0A0A0A]/90 flex items-end justify-center pb-4">
                        <Link to="/pricing"><Button size="sm" className="btn-primary"><Lock className="w-3 h-3 mr-2" />Upgrade to unlock</Button></Link>
                      </div>
                    </div>
                  )}
                  
                  {!generating.youtubeSeoTitles && !generatedContent.youtubeSeoTitles?.length && planFeatures?.youtube_outputs && (
                    <p className="text-sm text-neutral-500">Generate 10 SEO-optimized YouTube video title suggestions</p>
                  )}
                </div>
              </TabsContent>
            </Tabs>
          )}
        </div>
      </main>
    </div>
  );
};

export default EpisodeDetail;
